import React, { useEffect, useRef } from 'react';
import { heroData } from '../data/mockData';
import { ArrowRight, MousePointer2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const HeroSection = () => {
  const { lang, t } = useLanguage();
  const videoRef = useRef(null);
  const selectionFrameRef = useRef(null);
  const selectionCursorTrackRef = useRef(null);
  const selectionCursorRef = useRef(null);

  useEffect(() => {
    const frame = selectionFrameRef.current;
    const track = selectionCursorTrackRef.current;
    const cursor = selectionCursorRef.current;

    if (!frame || !track || !cursor) {
      return undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let animationFrameId;
    let cleanupResize = () => {};

    const easeInOutCubic = (value) => {
      if (value < 0.5) {
        return 4 * value * value * value;
      }
      return 1 - Math.pow(-2 * value + 2, 3) / 2;
    };

    const setFrameState = (progress) => {
      const scaleX = 0.07 + 0.93 * progress;
      const scaleY = 0.2 + 0.8 * progress;
      const fadeIn = Math.min(1, progress / 0.16);
      const borderAlpha = 0.28 + 0.32 * progress;
      const fillAlpha = 0.58 + 0.22 * progress;

      frame.style.opacity = String(fadeIn);
      frame.style.transform = `translateZ(0) scale3d(${scaleX}, ${scaleY}, 1)`;
      frame.style.borderColor = `rgba(161, 161, 170, ${borderAlpha})`;
      frame.style.backgroundColor = `rgba(255, 255, 255, ${fillAlpha})`;
    };

    const buildPath = (width, height, cursorWidth, cursorHeight) => {
      // Lucide MousePointer2 tip sits near (4.04, 4.69) on a 24x24 viewBox.
      const tipOffsetX = cursorWidth * (4.04 / 24);
      const tipOffsetY = cursorHeight * (4.69 / 24);
      // Border thickness is 2px; use centerline of border for the target corner.
      const borderCenterInset = 1;
      const endX = Math.max(width - borderCenterInset - tipOffsetX, 0);
      const endY = Math.max(height - borderCenterInset - tipOffsetY, 0);

      return [
        { x: -14, y: -16, angle: -14 },
        { x: width * 0.08, y: height * 0.03, angle: -13.2 },
        { x: width * 0.21, y: height * 0.11, angle: -12.4 },
        { x: width * 0.34, y: height * 0.22, angle: -11.5 },
        { x: width * 0.48, y: height * 0.34, angle: -10.7 },
        { x: width * 0.6, y: height * 0.46, angle: -10 },
        { x: width * 0.72, y: height * 0.58, angle: -9.3 },
        { x: width * 0.85, y: height * 0.72, angle: -8.6 },
        { x: width * 0.94, y: height * 0.85, angle: -8.2 },
        { x: endX, y: endY, angle: -8 }
      ];
    };

    const samplePath = (points, progress) => {
      if (progress <= 0) {
        return points[0];
      }

      if (progress >= 1) {
        return points[points.length - 1];
      }

      const scaled = progress * (points.length - 1);
      const index = Math.floor(scaled);
      const localProgress = scaled - index;
      const current = points[index];
      const next = points[index + 1];

      return {
        x: current.x + (next.x - current.x) * localProgress,
        y: current.y + (next.y - current.y) * localProgress,
        angle: current.angle + (next.angle - current.angle) * localProgress
      };
    };

    const setCursorState = (progress, points) => {
      const point = samplePath(points, progress);
      track.style.opacity = String(progress < 0.12 ? progress / 0.12 : 1);
      track.style.transform = `translate3d(${point.x}px, ${point.y}px, 0)`;
      cursor.style.transform = `translateZ(0) rotate(${point.angle}deg)`;
    };

    const setFinalStaticState = (points) => {
      const finalPoint = points && points.length ? points[points.length - 1] : null;

      frame.style.opacity = '1';
      frame.style.transform = 'translateZ(0) scale3d(1, 1, 1)';
      frame.style.borderColor = 'rgba(212, 212, 216, 1)';
      frame.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
      track.style.opacity = '1';
      if (finalPoint) {
        track.style.transform = `translate3d(${finalPoint.x}px, ${finalPoint.y}px, 0)`;
        cursor.style.transform = `translateZ(0) rotate(${finalPoint.angle}deg)`;
      } else {
        track.style.transform = 'translate3d(0, 0, 0)';
        cursor.style.transform = 'translateZ(0) rotate(-8deg)';
      }
    };

    if (reduceMotion) {
      const trackRect = track.getBoundingClientRect();
      const cursorRect = cursor.getBoundingClientRect();
      const reducedGeometry = buildPath(trackRect.width, trackRect.height, cursorRect.width, cursorRect.height);
      setFinalStaticState(reducedGeometry);
      return undefined;
    }

    const getPathLength = (points) => {
      if (!points || points.length < 2) {
        return 0;
      }

      let distance = 0;
      for (let i = 1; i < points.length; i += 1) {
        const deltaX = points[i].x - points[i - 1].x;
        const deltaY = points[i].y - points[i - 1].y;
        distance += Math.hypot(deltaX, deltaY);
      }

      return distance;
    };

    const getAdaptiveDuration = (points) => {
      const pathLength = getPathLength(points);
      return Math.min(1120, Math.max(760, pathLength * 2.2));
    };

    let durationMs = 740;
    const introDelayMs = 110;
    let animationStart = 0;
    let geometry = null;

    const updateGeometry = () => {
      const trackRect = track.getBoundingClientRect();
      const cursorRect = cursor.getBoundingClientRect();
      geometry = buildPath(trackRect.width, trackRect.height, cursorRect.width, cursorRect.height);
      durationMs = getAdaptiveDuration(geometry);
    };

    updateGeometry();

    const handleResize = () => {
      updateGeometry();
      if (geometry) {
        setCursorState(1, geometry);
      }
    };

    window.addEventListener('resize', handleResize, { passive: true });
    cleanupResize = () => window.removeEventListener('resize', handleResize);

    const animate = (timestamp) => {
      if (!animationStart) {
        animationStart = timestamp;
      }

      const elapsed = Math.max(0, timestamp - animationStart - introDelayMs);
      const linearProgress = Math.min(elapsed / durationMs, 1);
      const syncProgress = easeInOutCubic(linearProgress);

      setFrameState(syncProgress);
      if (geometry) {
        setCursorState(syncProgress, geometry);
      }

      if (linearProgress < 1) {
        animationFrameId = window.requestAnimationFrame(animate);
      } else {
        setFinalStaticState(geometry);
      }
    };

    setFrameState(0);
    if (geometry) {
      setCursorState(0, geometry);
    }
    animationFrameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      cleanupResize();
    };
  }, [lang]);

  const scrollToSection = (link) => {
    const id = link.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center pt-24 pb-8 px-6 bg-gradient-to-b from-blue-100 via-blue-50 to-blue-100/60 relative overflow-hidden">
      <div className="max-w-7xl w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12">
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-8 text-left">
            {/* Title */}
            <h1 className="leading-tight">
              <div
                className="text-zinc-950 font-black uppercase tracking-tight"
                style={{
                  fontSize: 'clamp(2rem, 7vw, 4.5rem)',
                  fontStretch: 'condensed',
                  letterSpacing: '-0.02em'
                }}
              >
                {t('hero.titleLine1')}
              </div>
              <div className="pt-2">
                <div className="relative inline-flex w-fit">
                  <div
                    ref={selectionFrameRef}
                    aria-hidden="true"
                    className="selection-frame pointer-events-none absolute -inset-x-3 -inset-y-2 rounded-xl border-2 border-zinc-300 bg-white/80 shadow-[0_18px_40px_rgba(15,23,42,0.08)]"
                  />
                  <span
                    className="relative z-10 px-2 py-1 text-zinc-600 font-black italic uppercase"
                    style={{
                      fontSize: 'clamp(2rem, 7vw, 4.5rem)',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {t('hero.titleHighlight')}
                  </span>
                  <div
                    ref={selectionCursorTrackRef}
                    aria-hidden="true"
                    className="selection-cursor-track pointer-events-none absolute -inset-x-3 -inset-y-2 z-20 overflow-visible"
                  >
                    <MousePointer2
                      ref={selectionCursorRef}
                      className="selection-cursor absolute left-0 top-0 h-8 w-8 text-zinc-800 md:h-10 md:w-10"
                      strokeWidth={2.25}
                      vectorEffect="non-scaling-stroke"
                      shapeRendering="geometricPrecision"
                    />
                  </div>
                </div>
              </div>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-zinc-600 leading-relaxed max-w-2xl">
              {t('hero.subtitle')}
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection('#portfolio')}
                className="group px-7 py-3.5 bg-zinc-950 text-white rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:bg-zinc-800 hover:shadow-xl hover:shadow-zinc-900/20 text-sm md:text-base"
              >
                <span className="flex items-center gap-2">
                  {t('hero.viewProjects')}
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
              <button
                onClick={() => scrollToSection('#contacto')}
                className="group px-7 py-3.5 bg-white text-zinc-900 border-2 border-zinc-300 rounded-full font-semibold transition-all duration-300 hover:bg-zinc-100 hover:border-zinc-400 text-sm md:text-base"
              >
                {t('hero.contact')}
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-gray-200">
              <div className="space-y-1">
                <div className="text-2xl md:text-3xl font-bold text-zinc-950">+75M</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('hero.statVisits')}</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl md:text-3xl font-bold text-zinc-950">+45</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('hero.statClients')}</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl md:text-3xl font-bold text-zinc-950">5.0★</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('hero.statRating')}</div>
              </div>
            </div>
          </div>

          {/* Right Content - Video */}
          <div className="relative">
            <div className="relative bg-black rounded-3xl overflow-hidden border border-zinc-300/60 shadow-[0_24px_60px_rgba(15,23,42,0.12)] transition-all duration-300 hover:-translate-y-1">
              <iframe
                ref={videoRef}
                className="w-full aspect-video"
                src={heroData.videoUrl}
                title="Jhon Cesar - Historias que venden"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Always visible */}
        <div className="flex flex-col items-center animate-bounce">
          <span className="text-sm text-zinc-500 uppercase tracking-widest mb-2">{t('hero.scrollCue')}</span>
          <div className="w-px h-12 bg-gradient-to-b from-zinc-400 to-transparent"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;